import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { MapPin, Locate, Loader2 } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export interface LocationData {
  lat: number;
  lng: number;
  radius: number; // in meters
}

interface LocationPickerProps {
  value: LocationData | null;
  onChange: (location: LocationData | null) => void;
}

export const LocationPicker = ({ value, onChange }: LocationPickerProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [radius, setRadius] = useState(value?.radius || 200);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const updateMapMarker = useCallback((lat: number, lng: number, r: number) => {
    const map = mapInstance.current;
    if (!map) return;

    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      markerRef.current = L.marker([lat, lng], {
        draggable: true,
        icon: L.divIcon({
          className: "custom-marker",
          html: `<div style="background: hsl(var(--primary)); width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"></div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        }),
      }).addTo(map);

      markerRef.current.on("dragend", () => {
        const pos = markerRef.current!.getLatLng();
        onChange({ lat: pos.lat, lng: pos.lng, radius: r });
        if (circleRef.current) {
          circleRef.current.setLatLng(pos);
        }
      });
    }

    if (circleRef.current) {
      circleRef.current.setLatLng([lat, lng]).setRadius(r);
    } else {
      circleRef.current = L.circle([lat, lng], {
        radius: r,
        color: "hsl(var(--primary))",
        fillColor: "hsl(var(--primary))",
        fillOpacity: 0.15,
        weight: 2,
      }).addTo(map);
    }

    map.setView([lat, lng], 15);
  }, [onChange]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    const map = L.map(mapRef.current, {
      center: value ? [value.lat, value.lng] : [-14.235, -51.9253],
      zoom: value ? 15 : 4,
      zoomControl: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    map.on("click", (e: L.LeafletMouseEvent) => {
      const newLoc = { lat: e.latlng.lat, lng: e.latlng.lng, radius };
      onChange(newLoc);
      updateMapMarker(e.latlng.lat, e.latlng.lng, radius);
    });

    mapInstance.current = map;

    if (value) {
      updateMapMarker(value.lat, value.lng, value.radius);
    }

    // Fix map container size
    setTimeout(() => map.invalidateSize(), 100);

    return () => {
      map.remove();
      mapInstance.current = null;
      markerRef.current = null;
      circleRef.current = null;
    };
  }, []);

  // Update circle radius when slider changes
  useEffect(() => {
    if (circleRef.current) {
      circleRef.current.setRadius(radius);
    }
    if (value) {
      onChange({ ...value, radius });
    }
  }, [radius]);

  const handleLocateMe = async () => {
    if (!navigator.geolocation) return;
    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude, radius };
        onChange(loc);
        updateMapMarker(loc.lat, loc.lng, radius);
        setIsLocating(false);
      },
      () => {
        setIsLocating(false);
      },
      { enableHighAccuracy: true }
    );
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`
      );
      const data = await res.json();
      if (data.length > 0) {
        const { lat, lon } = data[0];
        const parsedLat = parseFloat(lat);
        const parsedLng = parseFloat(lon);
        const loc = { lat: parsedLat, lng: parsedLng, radius };
        onChange(loc);
        updateMapMarker(parsedLat, parsedLng, radius);
      }
    } catch {
      // silently fail
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="space-y-3">
      <Label className="font-semibold flex items-center gap-2">
        <MapPin className="h-4 w-4" />
        Localização permitida
      </Label>

      {/* Search bar */}
      <div className="flex gap-2">
        <Input
          placeholder="Buscar endereço..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleSearch())}
          className="flex-1"
        />
        <Button
          type="button"
          variant="secondary"
          size="icon"
          onClick={handleSearch}
          disabled={isSearching}
        >
          {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="icon"
          onClick={handleLocateMe}
          disabled={isLocating}
        >
          {isLocating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Locate className="h-4 w-4" />}
        </Button>
      </div>

      {/* Map */}
      <div
        ref={mapRef}
        className="w-full h-[220px] rounded-lg border border-border overflow-hidden"
        style={{ zIndex: 0 }}
      />

      {/* Radius slider */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Raio de aceitação</span>
          <span className="font-semibold">{radius}m</span>
        </div>
        <Slider
          value={[radius]}
          onValueChange={(v) => setRadius(v[0])}
          min={50}
          max={2000}
          step={50}
        />
      </div>

      {value && (
        <p className="text-xs text-muted-foreground text-center">
          📍 {value.lat.toFixed(5)}, {value.lng.toFixed(5)} — raio de {radius}m
        </p>
      )}

      {!value && (
        <p className="text-xs text-muted-foreground text-center">
          Clique no mapa ou use os botões para definir a localização
        </p>
      )}
    </div>
  );
};
